import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { expensesApi, vehiclesApi, formatApiError } from '../services/api';
import {
  Wrench,
  Car,
  CalendarBlank,
  FunnelSimple,
  Plus,
  X,
  Check,
  Trash,
  CaretDown,
  CaretRight,
  Gauge
} from '@phosphor-icons/react';
import KilometrajeTab from './expenses/KilometrajeTab';
import MaintenanceTab from './expenses/MaintenanceTab';

const EXPENSE_TYPES = [
  'KIT Menor',
  'KIT Mayor',
  'Balatas',
  'Bujías',
  'Aceite Motor',
  'Llantas',
  'Amortiguadores',
  'Frenos',
  'Batería',
  'Clutch',
  'Transmisión',
  'Seguro',
  'Verificación',
  'Tenencia',
  'GPS',
  'Lavado',
  'Otro'
];

const CATEGORIES = ['FIJO', 'VARIABLE'];
const FREQUENCIES = ['unica', 'mensual', 'trimestral', 'semestral', 'anual'];

const INITIAL_FORM = {
  vehicle_id: '',
  expense_type: '',
  custom_type: '',
  category: 'VARIABLE',
  amount: '',
  date: new Date().toISOString().split('T')[0],
  frequency: 'unica',
  notes: ''
};

const Expenses = () => {
  const [activeTab, setActiveTab] = useState('egresos');
  const [expenses, setExpenses] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterVehicle, setFilterVehicle] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [expandedVehicle, setExpandedVehicle] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [expRes, vehRes] = await Promise.all([
        expensesApi.getAll(),
        vehiclesApi.getAll()
      ]);
      setExpenses(expRes.data);
      setVehicles(vehRes.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!formData.vehicle_id) return setFormError('Selecciona un vehiculo');
    if (!formData.expense_type && !formData.custom_type) return setFormError('Selecciona o escribe un tipo de egreso');
    if (!formData.amount || parseFloat(formData.amount) <= 0) return setFormError('Ingresa un monto valido');
    if (!formData.date) return setFormError('Selecciona una fecha');

    try {
      setSaving(true);
      const payload = {
        ...formData,
        expense_type: formData.expense_type === 'Otro' ? formData.custom_type : formData.expense_type,
        amount: parseFloat(formData.amount),
      };
      delete payload.custom_type;
      await expensesApi.create(payload);
      setShowModal(false);
      setFormData(INITIAL_FORM);
      await loadData();
    } catch (err) {
      setFormError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este egreso?')) return;
    try {
      await expensesApi.delete(id);
      await loadData();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  // Available months from data
  const months = [...new Set(expenses.filter(e => e.date).map(e => e.date.substring(0, 7)))].sort().reverse();

  const filtered = expenses.filter(e => {
    if (filterVehicle && e.vehicle_id !== filterVehicle) return false;
    if (filterMonth && e.date && !e.date.startsWith(filterMonth)) return false;
    return true;
  });

  const totalExpenses = filtered.reduce((sum, e) => sum + (e.amount || 0), 0);

  const byType = {};
  filtered.forEach(e => {
    const type = e.expense_type || 'Otro';
    byType[type] = (byType[type] || 0) + (e.amount || 0);
  });

  // Group by vehicle for summary
  const byVehicle = {};
  filtered.forEach(e => {
    const key = e.vehicle_id || 'sin_vehiculo';
    const name = e.vehicle_name || 'Sin vehiculo';
    if (!byVehicle[key]) byVehicle[key] = { name, plates: e.vehicle_plates || '', total: 0, count: 0 };
    byVehicle[key].total += (e.amount || 0);
    byVehicle[key].count += 1;
  });

  // Build vehicle-month matrix (uses ALL expenses, ignoring filters)
  const vehicleMonthMatrix = {};
  const allMonthsSet = new Set();
  expenses.forEach(e => {
    const vid = e.vehicle_id || 'sin_vehiculo';
    const vName = e.vehicle_name || 'Sin vehiculo';
    const vPlates = e.vehicle_plates || '';
    const cName = e.client_name || '';
    const monthKey = e.date ? e.date.substring(0, 7) : null;
    if (!monthKey) return;
    allMonthsSet.add(monthKey);
    if (!vehicleMonthMatrix[vid]) {
      vehicleMonthMatrix[vid] = { name: vName, plates: vPlates, client: cName, months: {}, total: 0, details: {} };
    } else if (!vehicleMonthMatrix[vid].client && cName) {
      vehicleMonthMatrix[vid].client = cName;
    }
    if (!vehicleMonthMatrix[vid].months[monthKey]) {
      vehicleMonthMatrix[vid].months[monthKey] = 0;
      vehicleMonthMatrix[vid].details[monthKey] = [];
    }
    vehicleMonthMatrix[vid].months[monthKey] += (e.amount || 0);
    vehicleMonthMatrix[vid].total += (e.amount || 0);
    vehicleMonthMatrix[vid].details[monthKey].push(e);
  });
  const allMonthsSorted = [...allMonthsSet].sort();
  // Month column totals
  const monthTotals = {};
  allMonthsSorted.forEach(m => {
    monthTotals[m] = Object.values(vehicleMonthMatrix).reduce((s, v) => s + (v.months[m] || 0), 0);
  });

  const fmt = (n) => `$${(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  const byTypeSorted = useMemo(
    () => Object.entries(byType).sort((a, b) => b[1] - a[1]),
    [byType]
  );

  const vehicleMatrixSorted = useMemo(
    () => Object.entries(vehicleMonthMatrix).sort((a, b) => b[1].total - a[1].total),
    [vehicleMonthMatrix]
  );

  const MONTH_NAMES = {
    '01': 'Enero', '02': 'Febrero', '03': 'Marzo', '04': 'Abril',
    '05': 'Mayo', '06': 'Junio', '07': 'Julio', '08': 'Agosto',
    '09': 'Septiembre', '10': 'Octubre', '11': 'Noviembre', '12': 'Diciembre'
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  const TABS = [
    { key: 'egresos', label: 'Egresos', icon: Wrench, testid: 'tab-egresos' },
    { key: 'kilometraje', label: 'Kilometraje', icon: Gauge, testid: 'tab-kilometraje' },
    { key: 'mantenimiento', label: 'Mantenimiento', icon: Car, testid: 'tab-mantenimiento' },
  ];

  return (
    <div data-testid="expenses-page">
      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-[#E2E8F0] mb-6">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = activeTab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              data-testid={t.testid}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
                active ? 'border-[#0055FF] text-[#0055FF]' : 'border-transparent text-[#64748B] hover:text-[#0F172A]'
              }`}
            >
              <Icon size={16} weight={active ? 'fill' : 'regular'} />
              {t.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'kilometraje' && <KilometrajeTab vehicles={vehicles} />}
      {activeTab === 'mantenimiento' && <MaintenanceTab vehicles={vehicles} />}
      {activeTab === 'egresos' && (
        <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-heading font-bold text-[#0F172A]">Egresos por Vehiculo</h1>
          <p className="text-sm text-[#64748B]">{filtered.length} gastos registrados</p>
        </div>
        <button
          onClick={() => { setFormData(INITIAL_FORM); setFormError(''); setShowModal(true); }}
          className="btn-primary flex items-center gap-2"
          data-testid="add-expense-btn"
        >
          <Plus size={18} />
          Registrar Egreso
        </button>
      </div>

      {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">{error}</div>}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="card p-5">
          <p className="text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1">Total Egresos</p>
          <p className="text-2xl font-bold text-[#E11D48]" data-testid="total-expenses">{fmt(totalExpenses)}</p>
        </div>
        <div className="card p-5">
          <p className="text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1">Registros</p>
          <p className="text-2xl font-bold text-[#0F172A]">{filtered.length}</p>
        </div>
        <div className="card p-5">
          <p className="text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1">Tipos de Gasto</p>
          <p className="text-2xl font-bold text-[#0F172A]">{Object.keys(byType).length}</p>
        </div>
      </div>

      {/* Breakdown by type */}
      {Object.keys(byType).length > 0 && (
        <div className="card p-5 mb-6">
          <h3 className="text-sm font-semibold text-[#0F172A] mb-3">Desglose por Tipo</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {byTypeSorted.map(([type, amount]) => (
              <div key={type} className="bg-[#F8FAFC] rounded-lg p-4 border border-[#E2E8F0]">
                <p className="text-xs text-[#64748B] mb-2 font-medium">{type}</p>
                <p className="text-lg font-bold text-[#0F172A]">{fmt(amount)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Vehicle Monthly Matrix */}
      {Object.keys(vehicleMonthMatrix).length > 0 && (
        <div className="card mb-6 overflow-hidden" data-testid="vehicle-month-matrix">
          <div className="p-5 border-b border-[#E2E8F0]">
            <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2">
              <Car size={16} className="text-[#64748B]" />
              Gasto por Vehiculo — Vista Mensual
            </h3>
            <p className="text-xs text-[#94A3B8] mt-1">Haz clic en un vehiculo para ver el detalle de gastos del mes</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                  <th className="text-left px-4 py-3 font-semibold text-[#475569] min-w-[200px]">Vehiculo</th>
                  {allMonthsSorted.map(m => {
                    const [y, mo] = m.split('-');
                    return (
                      <th key={m} className="text-right px-3 py-3 font-semibold text-[#475569] min-w-[100px]">
                        {MONTH_NAMES[mo]?.substring(0, 3)} {y.substring(2)}
                      </th>
                    );
                  })}
                  <th className="text-right px-4 py-3 font-bold text-[#0F172A] min-w-[110px] bg-[#F1F5F9]">Total</th>
                </tr>
              </thead>
              <tbody>
                {vehicleMatrixSorted
                  .map(([vid, vData]) => {
                    const isExpanded = expandedVehicle === vid;
                    const expandedMonth = isExpanded ? expandedVehicle : null;
                    return (
                      <React.Fragment key={vid}>
                        <tr
                          className="border-b border-[#E2E8F0] hover:bg-[#F8FAFC] cursor-pointer transition-colors"
                          onClick={() => setExpandedVehicle(isExpanded ? null : vid)}
                          data-testid={`vehicle-row-${vid}`}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {isExpanded ?
                                <CaretDown size={14} className="text-[#0055FF]" /> :
                                <CaretRight size={14} className="text-[#94A3B8]" />
                              }
                              <div>
                                <p className="font-medium text-[#0F172A]">{vData.name}</p>
                                <p className="text-xs text-[#94A3B8]">{vData.plates}</p>
                                {vData.client && (
                                  <p className="text-xs text-[#0055FF] font-medium truncate max-w-[220px]" title={vData.client}>
                                    {vData.client}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                          {allMonthsSorted.map(m => {
                            const val = vData.months[m] || 0;
                            return (
                              <td key={m} className={`text-right px-3 py-3 font-medium ${val > 0 ? 'text-[#E11D48]' : 'text-[#CBD5E1]'}`}>
                                {val > 0 ? fmt(val) : '-'}
                              </td>
                            );
                          })}
                          <td className="text-right px-4 py-3 font-bold text-[#E11D48] bg-[#FFF1F2]/30">
                            {fmt(vData.total)}
                          </td>
                        </tr>
                        {/* Expanded detail rows */}
                        {isExpanded && allMonthsSorted.map(m => {
                          const details = vData.details[m];
                          if (!details || details.length === 0) return null;
                          return details.map((exp, idx) => (
                            <tr key={`${m}-${idx}`} className="bg-blue-50/30 border-b border-[#E2E8F0]/50">
                              <td className="pl-12 pr-4 py-2">
                                <div className="flex items-center gap-2">
                                  <Wrench size={12} className="text-amber-500" />
                                  <span className="text-xs font-medium text-[#475569]">{exp.expense_type}</span>
                                  {exp.notes && <span className="text-xs text-[#94A3B8]">— {exp.notes}</span>}
                                </div>
                              </td>
                              {allMonthsSorted.map(cm => (
                                <td key={cm} className="text-right px-3 py-2 text-xs">
                                  {cm === m ? (
                                    <span className="font-medium text-[#E11D48]">{fmt(exp.amount)}</span>
                                  ) : ''}
                                </td>
                              ))}
                              <td className="text-right px-4 py-2 text-xs font-medium text-[#E11D48] bg-[#FFF1F2]/20">
                                {fmt(exp.amount)}
                              </td>
                            </tr>
                          ));
                        })}
                      </React.Fragment>
                    );
                  })}
                {/* Totals Row */}
                <tr className="bg-[#F1F5F9] border-t-2 border-[#CBD5E1]">
                  <td className="px-4 py-3 font-bold text-[#0F172A]">TOTAL</td>
                  {allMonthsSorted.map(m => (
                    <td key={m} className="text-right px-3 py-3 font-bold text-[#E11D48]">
                      {monthTotals[m] > 0 ? fmt(monthTotals[m]) : '-'}
                    </td>
                  ))}
                  <td className="text-right px-4 py-3 font-bold text-[#E11D48] bg-[#FFF1F2]/40 text-base">
                    {fmt(Object.values(monthTotals).reduce((s, v) => s + v, 0))}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <FunnelSimple size={18} className="text-[#64748B]" />
        <select
          value={filterVehicle}
          onChange={(e) => setFilterVehicle(e.target.value)}
          className="input-field max-w-xs text-sm"
          data-testid="filter-vehicle"
        >
          <option value="">Todos los vehiculos</option>
          {vehicles.map(v => (
            <option key={v.id} value={v.id}>
              {v.brand} {v.sub_brand} - {v.plates}
            </option>
          ))}
        </select>
        <CalendarBlank size={18} className="text-[#64748B]" />
        <select
          value={filterMonth}
          onChange={(e) => setFilterMonth(e.target.value)}
          className="input-field max-w-xs text-sm"
          data-testid="filter-month"
        >
          <option value="">Todos los meses</option>
          {months.map(m => {
            const [y, mo] = m.split('-');
            return <option key={m} value={m}>{MONTH_NAMES[mo]} {y}</option>;
          })}
        </select>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
        <table className="data-table" data-testid="expenses-table">
          <thead>
            <tr>
              <th style={{minWidth:'100px'}}>Fecha</th>
              <th style={{minWidth:'140px'}}>Vehiculo</th>
              <th style={{minWidth:'160px'}}>Cliente</th>
              <th style={{minWidth:'120px'}}>Tipo Egreso</th>
              <th style={{minWidth:'100px'}}>Categoria</th>
              <th style={{minWidth:'90px'}}>Monto</th>
              <th style={{minWidth:'90px'}}>Frecuencia</th>
              <th style={{minWidth:'120px'}}>Notas</th>
              <th style={{minWidth:'70px'}}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan="9" className="text-center py-8 text-[#64748B]">No hay egresos registrados</td></tr>
            ) : (
              filtered.map((exp) => (
                <tr key={exp.id || exp.excel_id}>
                  <td className="text-sm whitespace-nowrap">{fmtDate(exp.date)}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <Car size={16} className="text-[#64748B] flex-shrink-0" />
                      <div>
                        <span className="text-sm font-medium block">{exp.vehicle_name || '-'}</span>
                        <span className="text-xs text-[#64748B]">{exp.vehicle_plates || ''}</span>
                      </div>
                    </div>
                  </td>
                  <td className="text-sm text-[#0055FF] font-medium">{exp.client_name || <span className="text-[#CBD5E1]">—</span>}</td>
                  <td>
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 whitespace-nowrap">
                      <Wrench size={12} />
                      {exp.expense_type}
                    </span>
                  </td>
                  <td className="text-sm text-[#64748B] whitespace-nowrap">{exp.category || '-'}</td>
                  <td className="text-sm font-semibold text-[#E11D48] whitespace-nowrap">{fmt(exp.amount)}</td>
                  <td className="text-sm text-[#64748B] whitespace-nowrap">{exp.frequency || '-'}</td>
                  <td className="text-sm text-[#64748B] max-w-[150px] truncate">{exp.notes || '-'}</td>
                  <td>
                    <button
                      onClick={() => handleDelete(exp.id)}
                      className="p-1.5 text-[#64748B] hover:text-[#E11D48] hover:bg-red-50 rounded"
                      title="Eliminar"
                      data-testid={`delete-expense-${exp.id}`}
                    >
                      <Trash size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Create Expense Modal */}
      {showModal && (
        <div className="modal-overlay" data-testid="expense-modal">
          <div className="modal-content animate-fade-in max-w-lg">
            <div className="p-6 border-b border-[#E2E8F0]">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-heading font-bold">Registrar Egreso</h2>
                <button onClick={() => setShowModal(false)} className="p-2 text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded">
                  <X size={20} />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {formError && <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">{formError}</div>}

              {/* Vehicle */}
              <div>
                <label className="label">Vehiculo *</label>
                <select
                  value={formData.vehicle_id}
                  onChange={(e) => setFormData({ ...formData, vehicle_id: e.target.value })}
                  className="input-field w-full"
                  data-testid="expense-vehicle-select"
                >
                  <option value="">Seleccionar vehiculo...</option>
                  {vehicles.map(v => (
                    <option key={v.id} value={v.id}>
                      {v.brand} {v.sub_brand} - {v.plates}
                    </option>
                  ))}
                </select>
              </div>

              {/* Expense Type */}
              <div>
                <label className="label">Tipo de Egreso *</label>
                <select
                  value={formData.expense_type}
                  onChange={(e) => setFormData({ ...formData, expense_type: e.target.value })}
                  className="input-field w-full"
                  data-testid="expense-type-select"
                >
                  <option value="">Seleccionar tipo...</option>
                  {EXPENSE_TYPES.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                {formData.expense_type === 'Otro' && (
                  <input
                    type="text"
                    placeholder="Especificar tipo de egreso..."
                    value={formData.custom_type}
                    onChange={(e) => setFormData({ ...formData, custom_type: e.target.value })}
                    className="input-field w-full mt-2"
                    data-testid="expense-custom-type"
                  />
                )}
              </div>

              {/* Amount and Date */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Monto *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="input-field w-full"
                    placeholder="0.00"
                    data-testid="expense-amount"
                  />
                </div>
                <div>
                  <label className="label">Fecha *</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="input-field w-full"
                    data-testid="expense-date"
                  />
                </div>
              </div>

              {/* Category and Frequency */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Categoria</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="input-field w-full"
                    data-testid="expense-category"
                  >
                    {CATEGORIES.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">Frecuencia</label>
                  <select
                    value={formData.frequency}
                    onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                    className="input-field w-full"
                    data-testid="expense-frequency"
                  >
                    {FREQUENCIES.map(f => (
                      <option key={f} value={f}>{f.charAt(0).toUpperCase() + f.slice(1)}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="label">Notas</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="input-field w-full"
                  rows="2"
                  placeholder="Descripcion del gasto (opcional)"
                  data-testid="expense-notes"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">Cancelar</button>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary flex items-center gap-2"
                  data-testid="save-expense-btn"
                >
                  {saving ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Check size={18} />
                  )}
                  Guardar Egreso
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
        </div>
      )}
    </div>
  );
};

export default Expenses;
